import * as React from 'react';
import { Loader2 } from 'lucide-react';
import type {
  EnterpriseDetail,
  EnterpriseEnumValue,
  EnterpriseListItem,
} from '@proptr/shared';
import { Card } from '../../components/ui/card.js';
import { Label } from '../../components/ui/label.js';
import { Select } from '../../components/ui/select.js';
import { client } from '../../main.js';
import { useAgencyContext } from '../AgencyDetailPage.js';

// Option keys (legacy `value` strings on the enum object). "alias_*" is a
// virtual key for the alias case which has two enabled variants
// distinguished by a `capitalize` flag.
type ToggleKey = 'disabled' | 'enabled';
type AliasKey = 'disabled' | 'enabled_capitalize' | 'enabled_lowercase';

const TOGGLE_OPTIONS: { value: ToggleKey; label: string }[] = [
  { value: 'disabled', label: 'Disabled' },
  { value: 'enabled', label: 'Enabled' },
];

const ALIAS_OPTIONS: { value: AliasKey; label: string }[] = [
  { value: 'disabled', label: 'Disabled' },
  { value: 'enabled_capitalize', label: 'Enabled (capitalize)' },
  { value: 'enabled_lowercase', label: 'Enabled (lowercase)' },
];

function toToggleKey(v: EnterpriseEnumValue | null | undefined): ToggleKey {
  return v?.value === 'enabled' ? 'enabled' : 'disabled';
}
function fromToggleKey(
  v: ToggleKey,
  enabledLabel: string,
): EnterpriseEnumValue {
  return v === 'enabled'
    ? { id: 2, name: enabledLabel, value: 'enabled' }
    : { id: 1, name: 'Disabled', value: 'disabled' };
}
function toAliasKey(v: EnterpriseEnumValue | null | undefined): AliasKey {
  if (v?.value !== 'enabled') return 'disabled';
  return v.capitalize ? 'enabled_capitalize' : 'enabled_lowercase';
}
function fromAliasKey(v: AliasKey): EnterpriseEnumValue {
  if (v === 'disabled')
    return { id: 1, name: 'Disabled', value: 'disabled' };
  if (v === 'enabled_capitalize')
    return {
      id: 2,
      name: 'Enabled (capitalize)',
      value: 'enabled',
      capitalize: true,
    };
  return {
    id: 3,
    name: 'Enabled (lowercase)',
    value: 'enabled',
    capitalize: false,
  };
}

export function ProptreeSettingsView() {
  const { enterpriseId, agency, setAgency } = useAgencyContext();
  const [enterprises, setEnterprises] = React.useState<EnterpriseListItem[]>(
    [],
  );

  React.useEffect(() => {
    let alive = true;
    client.admin
      .listEnterprises({ limit: 200 })
      .then((res) => {
        if (alive) setEnterprises(res.items);
      })
      .catch(() => {
        // Non-fatal — dropdown will just be empty.
      });
    return () => {
      alive = false;
    };
  }, []);

  if (!agency) {
    return (
      <Card className="p-6">
        <div className="font-mono text-[12px] text-muted-foreground">
          Loading…
        </div>
      </Card>
    );
  }
  if (!agency.proptreeSwitch) {
    return (
      <Card className="p-6">
        <div className="font-mono text-[12px] text-muted-foreground">
          Proptree is not enabled for this enterprise — settings hidden.
        </div>
      </Card>
    );
  }

  return (
    <ProptreeForm
      enterpriseId={enterpriseId}
      agency={agency}
      enterprises={enterprises}
      onUpdate={(next) => setAgency(next)}
    />
  );
}

function ProptreeForm({
  enterpriseId,
  agency,
  enterprises,
  onUpdate,
}: {
  enterpriseId: string;
  agency: EnterpriseDetail;
  enterprises: EnterpriseListItem[];
  onUpdate: (next: EnterpriseDetail) => void;
}) {
  const [saving, setSaving] = React.useState<Set<string>>(() => new Set());
  const [saveError, setSaveError] = React.useState<string | null>(null);

  async function patch(
    key: keyof EnterpriseDetail,
    value: EnterpriseEnumValue | null,
  ) {
    setSaveError(null);
    setSaving((prev) => {
      const next = new Set(prev);
      next.add(String(key));
      return next;
    });
    onUpdate({ ...agency, [key]: value } as EnterpriseDetail);
    try {
      await client.admin.updateEnterprise(enterpriseId, { [key]: value });
    } catch (err) {
      setSaveError((err as Error).message);
      try {
        const fresh = await client.admin.getEnterprise(enterpriseId);
        onUpdate(fresh);
      } catch {
        /* noop */
      }
    } finally {
      setSaving((prev) => {
        const next = new Set(prev);
        next.delete(String(key));
        return next;
      });
    }
  }

  const linkedOptions: { value: string; label: string }[] = [
    { value: '__none__', label: 'None' },
    ...enterprises
      .filter((e) => e._id !== enterpriseId)
      .map((e) => ({
        value: e._id,
        label: e.companyName || e.name || e._id,
      })),
  ];

  const linkedValue: string =
    (agency.proptreeExtraEnterprise as unknown as { id?: string } | null)?.id ??
    '__none__';

  return (
    <Card className="p-6">
      <div className="space-y-6">
        <SectionHeading title="Proptree settings" />

        <div className="grid gap-4 md:grid-cols-3">
          <Field
            label="Auto-create Proptree"
            sublabel="(new agent)"
            saving={saving.has('autoCreateProptree')}
          >
            <Select<ToggleKey>
              options={TOGGLE_OPTIONS}
              value={toToggleKey(agency.autoCreateProptree)}
              onChange={(v) =>
                patch('autoCreateProptree', fromToggleKey(v, 'Enabled'))
              }
            />
          </Field>

          <Field
            label="Auto-enable Proptree+"
            sublabel="(new agent)"
            saving={saving.has('autoSubscribeProptreeAgent')}
          >
            <Select<ToggleKey>
              options={TOGGLE_OPTIONS}
              value={toToggleKey(agency.autoSubscribeProptreeAgent)}
              onChange={(v) =>
                patch(
                  'autoSubscribeProptreeAgent',
                  fromToggleKey(v, 'Enabled'),
                )
              }
            />
          </Field>

          <Field
            label="Auto-create shortname"
            sublabel="(new agent)"
            saving={saving.has('autoAliasProptree')}
          >
            <Select<AliasKey>
              options={ALIAS_OPTIONS}
              value={toAliasKey(agency.autoAliasProptree)}
              onChange={(v) =>
                patch('autoAliasProptree', fromAliasKey(v))
              }
            />
          </Field>
        </div>

        <Field
          label="Linked enterprise"
          sublabel="(sponsored feed)"
          saving={saving.has('proptreeExtraEnterprise')}
        >
          <Select<string>
            options={linkedOptions}
            value={linkedValue}
            onChange={(v) => {
              if (v === '__none__') {
                patch('proptreeExtraEnterprise', null);
              } else {
                const ent = enterprises.find((e) => e._id === v);
                if (!ent) return;
                patch('proptreeExtraEnterprise', {
                  id: ent._id as unknown as number,
                  name: ent.companyName || ent.name || ent._id,
                  value: ent._id,
                });
              }
            }}
          />
        </Field>

        <SectionHeading title="Auto notifications" />

        <div className="grid gap-4 md:grid-cols-4">
          <Field
            label="Welcome WhatsApp"
            saving={saving.has('autoSendProptreeWelcome')}
          >
            <Select<ToggleKey>
              options={TOGGLE_OPTIONS}
              value={toToggleKey(agency.autoSendProptreeWelcome)}
              onChange={(v) =>
                patch('autoSendProptreeWelcome', fromToggleKey(v, 'Enabled'))
              }
            />
          </Field>
          <Field
            label="Welcome Email"
            saving={saving.has('autoSendProptreeWelcomeEmail')}
          >
            <Select<ToggleKey>
              options={TOGGLE_OPTIONS}
              value={toToggleKey(agency.autoSendProptreeWelcomeEmail)}
              onChange={(v) =>
                patch(
                  'autoSendProptreeWelcomeEmail',
                  fromToggleKey(v, 'Enabled'),
                )
              }
            />
          </Field>
          <Field
            label="Transfer WhatsApp"
            saving={saving.has('autoSendProptreeTransfer')}
          >
            <Select<ToggleKey>
              options={TOGGLE_OPTIONS}
              value={toToggleKey(agency.autoSendProptreeTransfer)}
              onChange={(v) =>
                patch('autoSendProptreeTransfer', fromToggleKey(v, 'Enabled'))
              }
            />
          </Field>
          <Field
            label="Disable WhatsApp"
            saving={saving.has('autoSendProptreeDisable')}
          >
            <Select<ToggleKey>
              options={TOGGLE_OPTIONS}
              value={toToggleKey(agency.autoSendProptreeDisable)}
              onChange={(v) =>
                patch('autoSendProptreeDisable', fromToggleKey(v, 'Enabled'))
              }
            />
          </Field>
        </div>

        {saveError && (
          <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 font-mono text-[11px] text-destructive">
            {saveError}
          </div>
        )}
      </div>
    </Card>
  );
}

function SectionHeading({ title }: { title: string }) {
  return (
    <h2 className="font-display text-[20px] font-medium leading-none tracking-[-0.01em]">
      {title}
    </h2>
  );
}

function Field({
  label,
  sublabel,
  saving,
  children,
}: {
  label: string;
  sublabel?: string;
  saving?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-2">
        <Label className="font-sans text-[13px] font-medium normal-case tracking-normal text-foreground">
          {label}
          {sublabel && (
            <span className="ml-1 font-mono text-[10px] uppercase tracking-[0.08em] text-muted-foreground">
              {sublabel}
            </span>
          )}
        </Label>
        {saving && (
          <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
        )}
      </div>
      {children}
    </div>
  );
}
